import { useCallback, useContext, useEffect, useState } from "react";
import { getUser } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import { Userpilot } from "userpilot";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";

export const useAccount = () => {
  const { account, setAccount } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const { boot } = useIntercom();
  const { store } = useStore();

  const bootIntercomForAccount = (account: IAccount) => {
    if (!intercomKey) {
      return;
    }

    if (!account.active_subscription){
      return;
    }

    boot({
      email: account.email,
      userId: "frontend_boilerplate_" + account.id.toString(),
      name: account.name,
      customAttributes: {
        app: "frontend_boilerplate",
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    });
  };

  const bootIntercom = () => (account ? bootIntercomForAccount(account) : null);

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);
    if (acc) {
      Userpilot.identify(acc.id.toString(), {
        email: acc.email,
        created_at: acc.created_at,
        name: acc.name,
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
      });
      if (acc.active_subscription || DISABLE_PAYMENTS) {
        bootIntercomForAccount(acc);
      }
    }
  };

  const getAccount = async () => {
    setIsLoading(true);
    try {
      const profile = (await getUser()) as IAccount;
      setAccountWithThirdParties(profile);
      return profile;
    } finally {
      // Must clear on the error path too (e.g. a logged-out visitor's 401) —
      // RouteGuard blocks on isLoading, so getting stuck true here would spin
      // the loading state forever instead of resolving to "not logged in".
      setIsLoading(false);
    }
  };
  // The catch() ensures failures during the silent background fetch don't
  // surface as unhandled promise rejections.
  const debouncedGetAccount = useCallback(
    _debounce(() => getAccount().catch(() => {}), 200),
    []
  );

  useEffect(() => {
    if (!account) debouncedGetAccount();
  }, []);

  return {
    account,
    isLoading,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    bootIntercom,
  };
};
